import { createSlice } from '@reduxjs/toolkit'
import { RootState } from '../store'
import { loadUserStoredData, storeIsFirstTime, storeUserId, clearUserStoredData } from './userThunk'
import { CONST } from '../../const/const'
import { userSliceIniState } from '../storeHelpers/userSlice/data'
import { IUserSliceIniState } from '../storeHelpers/userSlice/types'
import { ITeamMember } from '../../models/users/user.interface'
import { INotificationSettings } from '../../types/competitor_types'
import { ILegalClauseInterface } from '../../models/user-legal-clauses/user-legal-clauses.interface'
import { getUserFullName } from '../../helpers/helpers'
import { IGuestInterface } from '../../models/guests/guest.interface'
import { UserModel } from '../../models/users/user.model'

const userSlice = createSlice({
  name: 'user',
  initialState: userSliceIniState,
  reducers: {
    setUserLegalClauses: (
      state,
      {
        payload,
      }: {
        payload: {
          index?: null | number
          clause: ILegalClauseInterface | ILegalClauseInterface[]
        }
      }
    ) => {
      /**
       * If index is null, then add a new clause,
       * else modify the clause at index,
       * In clause arg when we send array, then it will overwrite clauses in state
       * */
      let { index, clause } = payload
      let modifiedClauses: ILegalClauseInterface[] = [...state.userLegalClauses]

      if (Array.isArray(clause)) modifiedClauses = clause
      else {
        if (typeof index === 'number') modifiedClauses.splice(index, 1, clause)
        else modifiedClauses.push(clause)
      }
      state.userLegalClauses = modifiedClauses
    },
    setProfileDetails(state, { payload }: { payload: IUserSliceIniState['profileDetails'] }) {
      state.profileDetails = payload
    },
    setUserId(state, { payload }) {
      state.userId = payload
    },
    clearUserId(state) {
      state.userId = null
    },
    setOrganizerProfileImageUrl(state, { payload }) {
      state.organiserProfileData.organizerProfileImageUrl = payload
    },
    setVendorProfileImageUrl(state, { payload }) {
      state.vendorProfileData.vendorProfileImageUrl = payload
    },
    setIsLoggedIn(state, { payload }) {
      state.isLoggedIn = payload
    },
    setShowRegistration(state, { payload }) {
      state.showRegistration = payload
    },
    setHorseTeamMembers(state, { payload }: { payload: ITeamMember[] }) {
      state.HorseTeamMembers = payload
    },
    setHorseIdList(state, { payload }: { payload: string[] }) {
      state.HorseTeamIdLIst = payload
    },
    setUserProfileComplete(state, { payload }) {
      state.profileCompleted = payload
    },
    setUserNotificationSettings(state, { payload }: { payload: INotificationSettings }) {
      state.userNotificationsSettings = payload
    },
    setUserDocumentsData(state, { payload }) {
      state.userDocumentsData = payload
    },
    resetUserData(state) {
      state.profileDetails = new UserModel()
    },
    setGuestData(state, { payload }: { payload: IGuestInterface }) {
      state.guestId = payload.id!
    },
    setPaymentStatus(state, { payload }: { payload: boolean }) {
      state.paymentStatus = payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadUserStoredData.fulfilled, (state, action) => {
      state.userId = action.payload[CONST.STORAGE.LATEST.USERID.KEY]
      state.isFirstTime = action.payload[CONST.STORAGE.LATEST.FIRST_TIME.KEY] === 'true'
    })

    builder.addCase(storeUserId.fulfilled, (state, action) => {
      state.userId = action.payload
    })

    builder.addCase(storeIsFirstTime.fulfilled, (state, action) => {
      state.isFirstTime = action.payload
    })

    builder.addCase(clearUserStoredData.fulfilled, (state) => {
      state.userId = null
      state.profileDetails = new UserModel()
      state.organiserProfileData.organizerProfileImageUrl = null
      state.vendorProfileData.vendorProfileImageUrl = null
      state.isLoggedIn = false
    })
  },
})

export { loadUserStoredData, storeUserId, storeIsFirstTime, clearUserStoredData }

export const {
  setUserId,
  setUserProfileComplete,
  setUserDocumentsData,
  setShowRegistration,
  setIsLoggedIn,
  setProfileDetails,
  setUserLegalClauses,
  setUserNotificationSettings,
  resetUserData,
  setGuestData,
  setPaymentStatus,
  setHorseTeamMembers,
} = userSlice.actions

export const selectUserId = (state: RootState) => state.user.userId
export const selectGuestId = (state: RootState) => state.user.guestId

export const selectIsLoggedIn = (state: RootState) => state.user.isLoggedIn
export const selectIsFirstTime = (state: RootState) => state.user.isFirstTime
export const selectUserLegalClauses = (state: RootState) => state.user.userLegalClauses
export const selectShowRegistration = (state: RootState) => state.user.showRegistration
export const selectProfileCompleted = (state: RootState) => state.user.profileCompleted
export const selectProfileData = (state: RootState) => state.user.profileDetails
export const selectHorseTeamMembers = (state: RootState) => state.user?.HorseTeamMembers ?? []

export const getGuestData = (
  state: RootState
): IGuestInterface & {
  isLoggedIn: boolean
  isPlatformUser: boolean
  userProfilePic: string | null
  userId: string | null
} => {
  const user = state.user
  return {
    id: user.guestId!,
    userId: user.userId,
    fullName: user?.profileDetails ? getUserFullName(user.profileDetails) : null,
    userEmail: user.profileDetails?.userEmail ?? null,
    userName: user.profileDetails?.userName ?? null,
    userNameNGram: user.profileDetails?.userNameNGram ?? [],
    userFirstName: user.profileDetails?.userFirstName ?? null,
    userLastName: user.profileDetails?.userLastName ?? null,
    userProfilePic: user.profileDetails?.userProfilePicture ?? null,
    userAddress: user.profileDetails?.userAddress ?? null,
    isLoggedIn: !!user.isLoggedIn,
    isPlatformUser: !!user.userId,
  }
}
// organizer
export const selectpaymentStatus = (state: RootState) => state.user.paymentStatus
export const selectUserReducer = (state: RootState) => state.user
export const selectUserNotificationsSettings = (state: RootState) =>
  state.user.userNotificationsSettings

export default userSlice.reducer
